import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useAuditLogs, useClearDatabase, useVerifyAuditChain } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { formatDisplayDateTime } from '../utils/dateUtils';
import PageHeader from '../components/ui/PageHeader';
import { glassCardSx, glassDialogPaperProps } from '../styles/tokens';

export default function AuditLogPage() {
  const { user } = useAuth();
  const logs = useAuditLogs(1, 500);
  const verify = useVerifyAuditChain();
  const clearDb = useClearDatabase();
  const [filter, setFilter] = useState('');
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const rows = useMemo(() => {
    const data = logs.data ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (l) =>
        l.user_email?.toLowerCase().includes(q) ||
        l.action?.toLowerCase().includes(q) ||
        (l.reason ?? '').toLowerCase().includes(q)
    );
  }, [logs.data, filter]);

  const handleExportCsv = () => {
    const data = logs.data ?? [];
    if (data.length === 0) return;

    const headers = ['Timestamp (UTC)', 'User Email', 'Action', 'Record ID', 'Reason'];
    const csvRows = [headers.join(',')];
    for (const log of data) {
      csvRows.push(
        [
          `"${new Date(log.timestamp).toISOString()}"`,
          `"${log.user_email}"`,
          `"${log.action}"`,
          `"${log.document_id}"`,
          `"${(log.reason || 'N/A').replace(/"/g, '""')}"`,
        ].join(',')
      );
    }

    const encodedUri = encodeURI('data:text/csv;charset=utf-8,' + csvRows.join('\n'));
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `system_audit_log_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (user?.role !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Only administrators can view the audit trail.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader
        title="Audit Trail"
        subtitle="Tamper-evident record of every change, per 21 CFR Part 11."
        actions={
          <>
            <TextField
              size="small"
              placeholder="Filter by user / action / reason"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <Button
              variant="outlined"
              onClick={handleExportCsv}
              disabled={logs.isLoading || (logs.data ?? []).length === 0}
              startIcon={<i className="fas fa-file-csv" style={{ fontSize: '0.85rem' }} />}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              onClick={() => verify.mutate()}
              disabled={verify.isPending}
              startIcon={<i className="fas fa-shield-halved" style={{ fontSize: '0.85rem' }} />}
            >
              Verify chain
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setClearConfirmOpen(true)}
              disabled={clearDb.isPending}
              startIcon={<i className="fas fa-trash-can" style={{ fontSize: '0.85rem' }} />}
            >
              Clear database
            </Button>
          </>
        }
      />

      {clearDb.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Database cleared — records, box occupancy, audit logs and notifications removed. User
          accounts were preserved.
        </Alert>
      )}
      {clearDb.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(clearDb.error as any)?.response?.data?.detail ?? 'Failed to clear the database.'}
        </Alert>
      )}

      {verify.data && (
        <Alert severity={verify.data.valid ? 'success' : 'error'} sx={{ mb: 2 }}>
          {verify.data.valid
            ? `Audit chain intact (${verify.data.entries} entries).`
            : `Tampering detected at entry ${verify.data.broken_at}.`}
        </Alert>
      )}

      {logs.isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : logs.isError ? (
        <Alert severity="error">Failed to load audit logs.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ ...glassCardSx, p: 0, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Record</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Signed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((l) => (
                <TableRow key={l.id} hover>
                  <TableCell>{formatDisplayDateTime(l.timestamp)}</TableCell>
                  <TableCell>{l.user_email}</TableCell>
                  <TableCell>{l.action}</TableCell>
                  <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {l.document_id}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{l.reason}</TableCell>
                  <TableCell>
                    {l.signed ? (
                      <Chip size="small" color="primary" label={l.signature_meaning || 'signed'} />
                    ) : (
                      ''
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Clear-database confirmation (developer mode only) */}
      <Dialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        PaperProps={glassDialogPaperProps}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Clear database?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              This permanently deletes <strong>all cryopreservation records, box occupancy,
              audit logs and notifications</strong>, and resets the audit hash chain.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              User accounts and roles are <strong>not</strong> affected.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Available in developer mode only — the server refuses this in production.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setClearConfirmOpen(false)}
            sx={{ borderRadius: '10px', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={clearDb.isPending}
            onClick={() => {
              clearDb.mutate(undefined, { onSettled: () => setClearConfirmOpen(false) });
            }}
            sx={{ borderRadius: '10px', textTransform: 'none', px: 2.5 }}
          >
            {clearDb.isPending ? 'Clearing…' : 'Clear everything'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
